import { useCallback, useEffect, useRef, useState } from "react"
import toast from "react-hot-toast"

import { trpc } from "../../lib/trpc"
import type { IHomeDashboardLayout, IHomeDashboardPrefs } from "./homeDashboard.types"
import { EMPTY_LAYOUT, EMPTY_PREFS } from "./homeDashboardModel"

const CACHE_KEY = "home.prefs.v1"
const SAVE_DEBOUNCE_MS = 400

const readJson = <T>(key: string, fallback: T): T => {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    // Unreadable storage (private mode, corrupt value) falls back to defaults
    return fallback
  }
}

const writeJson = (key: string, value: unknown) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Storage full or blocked: the in-memory value still applies for this session
  }
}

/** Fills missing layout fields per view; a cached array from the old user-created views reads as none. */
const normalizeViews = (views: unknown): IHomeDashboardPrefs["views"] =>
  views && typeof views === "object" && !Array.isArray(views)
    ? Object.fromEntries(
        Object.entries(views as Record<string, Partial<IHomeDashboardLayout>>).map(([id, layout]) => [
          id,
          { ...EMPTY_LAYOUT, ...layout },
        ]),
      )
    : {}

/** Per-device UI state (collapsed widgets, column widths): read synchronously so the first paint is right. */
export const useDevicePref = <T>(key: string, initial: T): [T, (next: T) => void] => {
  const [value, setValue] = useState<T>(() => readJson(key, initial))
  const set = useCallback(
    (next: T) => {
      setValue(next)
      writeJson(key, next)
    },
    [key],
  )
  return [value, set]
}

/**
 * Cross-device layouts (Home plus each view in viewRegistry.ts: hidden, snoozed, placement, order). Paints from the local copy instantly, then
 * adopts the server copy unless the user already changed something this session. Saves are debounced.
 */
export const useHomePrefs = (): [
  IHomeDashboardPrefs,
  (update: (prev: IHomeDashboardPrefs) => IHomeDashboardPrefs) => void,
] => {
  const [prefs, setPrefs] = useState<IHomeDashboardPrefs>(() => {
    const cached = readJson(CACHE_KEY, EMPTY_PREFS)
    return { ...EMPTY_PREFS, ...cached, views: normalizeViews(cached.views) }
  })
  const dirty = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const server = trpc.homeDashboard.getPrefs.useQuery(undefined, { staleTime: 30_000 })
  const save = trpc.homeDashboard.savePrefs.useMutation({
    onError: (error) => toast.error(`Layout not saved: ${error.message}`),
  })
  const { mutate } = save

  useEffect(() => {
    if (!server.data || dirty.current) return
    const next = { ...EMPTY_PREFS, ...server.data, views: normalizeViews(server.data.views) }
    setPrefs(next)
    writeJson(CACHE_KEY, next)
  }, [server.data])

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const update = useCallback(
    (fn: (prev: IHomeDashboardPrefs) => IHomeDashboardPrefs) => {
      dirty.current = true
      setPrefs((prev) => {
        const next = fn(prev)
        writeJson(CACHE_KEY, next)
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => mutate(next), SAVE_DEBOUNCE_MS)
        return next
      })
    },
    [mutate],
  )

  return [prefs, update]
}
